// Admin → Invites (admins only): make an invite link (email optional, starting credits), copy it, see every invite
// and revoke the open ones. GET/POST /api/admin/invites, POST /api/admin/invites/:code/revoke.
import { useState, type FormEvent } from "react";
import { api } from "../api";
import { Empty, ErrorNote, fmtCredits, Icon, Sheet, Spinner, timeAgo, useLoad } from "../components/bits";
import { AccessRequests } from "../components/AccessRequests";
import { Shell } from "../components/Shell";
import { Link } from "../router";
import { toast, useStore } from "../store";
import type { Invite } from "../types";

const STATUS: Record<Invite["status"], { label: string; tone: string }> = {
  open: { label: "Open", tone: "busy" },
  used: { label: "Joined", tone: "done" },
  revoked: { label: "Revoked", tone: "bad" },
  expired: { label: "Expired", tone: "draft" },
};

async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast("Invite link copied.", "good");
  } catch {
    // clipboard blocked (http, or an old browser): select it for a manual copy
    window.prompt("Copy the invite link", text);
  }
}

export function InvitesScreen() {
  const me = useStore((s) => s.me);
  const admin = me?.user.role === "admin";
  const { data: invites, setData, error, loading, reload } = useLoad(() => (admin ? api.invites() : Promise.resolve([] as Invite[])), [admin]);
  const [email, setEmail] = useState("");
  const [credits, setCredits] = useState("500");
  const [creating, setCreating] = useState(false);
  const [made, setMade] = useState<{ code: string; url: string; email: string | null } | null>(null);
  const [revoking, setRevoking] = useState<Invite | null>(null);

  if (!admin)
    return (
      <Shell>
        <div className="page">
          <Empty title="Admins only" action={<Link to="/" className="btn btn-primary">Back home</Link>}>
            Invites are managed by the studio's admins.
          </Empty>
        </div>
      </Shell>
    );

  const create = async (e: FormEvent) => {
    e.preventDefault();
    const n = Math.round(Number(credits));
    if (!Number.isFinite(n) || n < 0) return toast("Credits must be a number, 0 or more.", "bad");
    setCreating(true);
    try {
      const r = await api.createInvite({ email: email.trim() || null, credits: n });
      setMade({ ...r, email: email.trim() || null });
      setEmail("");
      reload();
    } catch (err) {
      toast(`Couldn't make the invite. ${(err as Error).message}`, "bad");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (inv: Invite) => {
    setRevoking(null);
    const prev = invites;
    setData((invites ?? []).map((i) => (i.code === inv.code ? { ...i, status: "revoked" } : i)));
    try {
      await api.revokeInvite(inv.code);
      toast("Invite revoked. The link no longer works.", "good");
    } catch (err) {
      setData(prev);
      toast(`Couldn't revoke it. ${(err as Error).message}`, "bad");
    }
  };

  const open = invites?.filter((i) => i.status === "open").length ?? 0;
  return (
    <Shell>
      <div className="page invites">
        <header className="page-head">
          <Link to="/settings" className="back">
            <Icon name="back" size={18} /> Settings
          </Link>
          <h1 className="page-title">Invites</h1>
          <p className="page-lede">CAO Studio is invite-only. Each link creates one account with the credits you give it.</p>
        </header>

        <div className="invites-grid">
          <section className="panel invite-new" aria-labelledby="new-inv-h">
            <h2 id="new-inv-h" className="group-title">
              New invite
            </h2>
            <form className="invite-form" onSubmit={create}>
              <label className="field">
                <span className="field-label">Email (optional)</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Anyone with the link" inputMode="email" autoComplete="off" />
                <span className="field-hint">With an email, only that address can use it.</span>
              </label>
              <label className="field">
                <span className="field-label">Starting credits</span>
                <input type="number" min={0} max={100000} step={50} value={credits} onChange={(e) => setCredits(e.target.value)} inputMode="numeric" />
              </label>
              <button type="submit" className="btn btn-primary btn-block" disabled={creating}>
                <Icon name="plus" size={18} /> {creating ? "Making the link" : "Make invite link"}
              </button>
            </form>
            {made && (
              <div className="invite-made" role="status">
                <p className="panel-label">{made.email ? `Link for ${made.email}` : "Link for anyone"}</p>
                <div className="invite-link">
                  <code>{made.url}</code>
                  <button type="button" className="btn btn-primary btn-small" onClick={() => void copy(made.url)}>
                    <Icon name="copy" size={16} /> Copy
                  </button>
                </div>
              </div>
            )}
          </section>

          <AccessRequests />

          <section className="invite-list" aria-labelledby="inv-list-h">
            <header className="section-head">
              <h2 id="inv-list-h">All invites</h2>
              {invites && <span className="muted">{open} open</span>}
            </header>
            {error && <ErrorNote text={`Couldn't load the invites. ${error}`} retry={reload} />}
            {loading && !invites && <Spinner label="Loading" />}
            {invites && !invites.length && <p className="muted">No invites yet. Make the first one.</p>}
            {!!invites?.length && (
              <ul className="inv-rows">
                {invites.map((i) => (
                  <li key={i.code} className={`inv-row inv-${i.status}`}>
                    <div className="inv-main">
                      <p className="inv-who">{i.status === "used" && i.usedBy ? i.usedBy : (i.email ?? "Anyone with the link")}</p>
                      <p className="inv-meta">
                        <code>{i.code}</code> · {fmtCredits(i.credits)} credits{i.createdAt ? ` · ${timeAgo(i.createdAt)}` : ""}
                      </p>
                    </div>
                    <span className={`status status-${STATUS[i.status].tone} inv-status`}>{STATUS[i.status].label}</span>
                    <div className="inv-acts">
                      {i.status === "open" && (
                        <>
                          <button type="button" className="icon-btn" onClick={() => void copy(i.url)} aria-label={`Copy the invite link ${i.code}`} title="Copy link">
                            <Icon name="copy" size={18} />
                          </button>
                          <button type="button" className="btn btn-quiet btn-small" onClick={() => setRevoking(i)}>
                            Revoke
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        {revoking && (
          <Sheet title="Revoke this invite?" onClose={() => setRevoking(null)}>
            <div className="sheet-body confirm">
              <p>
                The link <code>{revoking.code}</code>
                {revoking.email ? ` for ${revoking.email}` : ""} stops working right away. Nobody has used it yet, so no account is affected.
              </p>
              <div className="confirm-actions">
                <button type="button" className="btn btn-quiet" onClick={() => setRevoking(null)}>
                  Keep it
                </button>
                <button type="button" className="btn btn-primary" onClick={() => void revoke(revoking)} data-autofocus>
                  Revoke
                </button>
              </div>
            </div>
          </Sheet>
        )}
      </div>
    </Shell>
  );
}
